<template>
    <Spinner v-if="displaySpinner" :text="loadingText" />
    <Page>
        <template #title>
            <Title :level="1" class="page-title">
                <i class="pi pi-check-square" />
                {{ taskName }}
            </Title>
            <Tag
                icon="pi pi-exclamation-triangle"
                severity="warn"
                value="Archived"
                v-if="isArchived"
            ></Tag>
        </template>
        <template #controls>
            <LastChangedInfo
                v-if="task?.identity"
                :entityId="task.identity"
                :entityType="EntityType.TASK"
            />
            <template v-if="isArchived">
                <Button
                    severity="danger"
                    icon="pi pi-trash"
                    label="Delete"
                    @click="handleDeleteArchive"
                />
            </template>
            <template v-else>
                <Button
                    v-if="description && !description.content && isEditing"
                    v-tooltip.bottom="{
                        value: 'Will generate a first draft of the description based on the context.',
                        showDelay: 100,
                    }"
                    severity="secondary"
                    icon="pi pi-sparkles"
                    :label="
                        description?.content
                            ? 'Regenerate'
                            : 'Generate first draft'
                    "
                    @click="generateTaskDescription"
                />
            </template>
        </template>
        <Section class="description" editable>
            <TextAppArtefact
                v-if="task?.identity && description"
                v-model="description"
                :entityId="task.identity"
                :entityType="EntityType.TASK"
                :isEditing="isEditing"
            />
        </Section>
        <Section v-if="task?.entity_refs?.length || isEditing" editable>
            <template #title>Linked Domain Objects</template>
            <BusinessEntityRefs
                v-if="task?.identity"
                :elementId="task.identity"
                :entityRefs="task.entity_refs ?? []"
            />
        </Section>
        <Section v-if="complianceEnabled" editable>
            <template #title>Compliance Evidence</template>
            <div class="evidence-section">
                <div class="evidence-toggle">
                    <ToggleSwitch
                        v-model="generatesEvidence"
                        :disabled="!isEditing"
                        inputId="generates-evidence"
                        class="compliancy-toggle"
                    />
                    <label for="generates-evidence">
                        This task generates evidence
                    </label>
                </div>
                <TextAppArtefact
                    v-if="
                        generatesEvidence &&
                        task?.identity &&
                        evidenceInstructions
                    "
                    v-model="evidenceInstructions"
                    :entityId="task.identity"
                    :entityType="EntityType.TASK"
                    :isEditing="isEditing"
                />
            </div>
        </Section>
        <Section
            v-if="task?.subtype?.toLowerCase().startsWith('user')"
            class="code-preview-section"
            editable
        >
            <div class="code-preview-header" v-if="isEditing">
                <Button
                    v-tooltip.bottom="{
                        value: 'Will generate a UI preview based on the context.',
                        showDelay: 100,
                    }"
                    severity="secondary"
                    icon="pi pi-sparkles"
                    badge="BETA"
                    badgeSeverity="primary"
                    :label="uiCode?.content ? 'Regenerate UI' : 'Generate UI'"
                    @click="generateTaskUI"
                />
            </div>
            <p v-if="uiCode?.content">
                <i>This is an AI-generated preview, results may vary.</i>
            </p>
            <CodePreview
                v-if="uiCode?.content"
                :code="uiCode.content"
                framework="react"
            />
            <div v-else class="empty-state">
                <i class="pi pi-objects-column empty-icon"></i>
                <div>
                    <h3>No UI generated yet</h3>
                    <p>
                        <i>
                            You can automatically generate a user interface for
                            this task in edit mode and preview it here.
                        </i>
                    </p>
                </div>
            </div>
        </Section>
        <Section editable>
            <Documents
                v-if="task"
                :isEditing="isEditing"
                entityType="task"
                :entity="task"
                :artefacts="artefacts"
            />
        </Section>
    </Page>
</template>

<script setup lang="ts">
import { EventSource } from "eventsource";
import ToggleSwitch from "primevue/toggleswitch";
import { useConfirm } from "primevue/useconfirm";
import { useToast } from "primevue/usetoast";
import { computed, onUnmounted, ref, watch } from "vue";

import BusinessEntityRefs from "@/components/BusinessEntityRefs.vue";
import CodePreview from "@/components/CodePreview.vue";
import Documents from "@/components/Documents.vue";
import LastChangedInfo from "@/components/LastChangedInfo.vue";
import Page from "@/components/Page.vue";
import Section from "@/components/Section.vue";
import TextAppArtefact from "@/components/TextAppArtefact.vue";
import Title from "@/components/Title.vue";
import { useEditManager } from "@/composables/edit-manager";
import { useGenerateDescriptionDialog } from "@/composables/generate-description";
import { useOrganizationManager } from "@/composables/organization-manager";
import { useStekManager } from "@/composables/stek-manager";
import config from "@/config";
import { useArtefactsManager } from "@/lib/artefacts/user";
import { EntityType } from "@/lib/openapi/gen/types.gen";
import { baseSuccessToastMessage } from "@/primevue/init";
import Spinner from "@/shared/Spinner.vue";
import { useAuthenticationStore } from "@/stores/authentication-store";
import { useTaskManager, useTaskStore } from "@/stores/task-store";

const LOADING_TEXTS_INTERVAL = 5000;
const LOADING_TEXTS = [
    "Teaching robots to draw buttons",
    "Convincing pixels to align perfectly",
    "Persuading CSS to behave",
    "Herding wild components into formation",
    "Negotiating with stubborn div elements",
    "Politely asking AI to be creative",
    "Converting coffee into UI code",
    "Summoning the interface gods",
    "Waiting for design inspiration to strike",
];

const toast = useToast();
const { deleteTask, updateEvidenceEnabled } = useTaskStore();
const { isEditing } = useEditManager();
const { isFetching, description, uiCode, evidenceInstructions, task } =
    useTaskManager();
const { artefacts } = useArtefactsManager();
const { generateContent } = useGenerateDescriptionDialog();
const { organization } = useOrganizationManager();
const { stek } = useStekManager();
const { getToken } = useAuthenticationStore();

const complianceEnabled = computed(
    () => stek.value?.compliance_enabled ?? false
);

const generatesEvidence = ref(false);

// Sync evidence state from task when it loads
watch(
    () => task.value,
    (newTask) => {
        if (newTask) {
            generatesEvidence.value = newTask.generates_evidence ?? false;
        }
    },
    { immediate: true }
);

// Save evidence toggle flag when editing stops and value changed
watch(isEditing, async (editing, wasEditing) => {
    if (wasEditing && !editing && task.value) {
        const changed =
            generatesEvidence.value !==
            (task.value.generates_evidence ?? false);
        if (changed) {
            await updateEvidenceEnabled(generatesEvidence.value);
        }
    }
});

const confirm = useConfirm();

const displaySpinner = ref(false);
const loadingText = ref(LOADING_TEXTS[0]);

const generateTaskUI = async () => {
    if (!task.value) {
        return;
    }
    try {
        displaySpinner.value = true;
        loadingText.value = "Generating User Interface, this may take a while";

        const token = await getToken();
        const eventSource = new EventSource(
            `${config.bekendURI}/process/process_element_mgt/generate_user_interface/start`,
            {
                fetch: (input, init) => {
                    return fetch(input, {
                        ...init,
                        method: "POST",
                        headers: {
                            ...init?.headers,
                            Authorization: `Bearer ${token}`,
                            Organization: organization?.value?.identity ?? "",
                            "Content-Type": "application/json",
                        },
                        body: JSON.stringify({
                            task_id: task.value?.identity,
                        }),
                    });
                },
            }
        );

        let codeContent = "";

        eventSource.addEventListener("open", () => {
            console.log("SSE connection opened");
            // Start rotating when the connection is active
            startRotateRandomText();
        });

        eventSource.addEventListener("message", (event) => {
            const data = JSON.parse(event.data);
            codeContent += data.content;
        });

        eventSource.addEventListener("error", (error) => {
            console.error("SSE error:", error);
            eventSource.close();
            toast.add({
                severity: "error",
                summary: "Could not generate UI preview",
            });
            stopRotateRandomText();
            displaySpinner.value = false;
        });

        eventSource.addEventListener("close", () => {
            eventSource.close();
            toast.add({
                ...baseSuccessToastMessage,
                summary: "UI preview generated successfully",
            });
            uiCode.value!.updateContent(codeContent);
            stopRotateRandomText();
            displaySpinner.value = false;
        });
    } catch (error) {
        console.error("Error setting up SSE:", error);
        toast.add({
            severity: "error",
            summary: "Could not generate UI preview",
        });
        stopRotateRandomText();
        displaySpinner.value = false;
    }
};

let loadingTextInterval: number | NodeJS.Timeout;

const startRotateRandomText = () => {
    loadingTextInterval = setInterval(() => {
        const randomValue = Math.round(Math.random() * LOADING_TEXTS.length);
        loadingText.value = LOADING_TEXTS[randomValue];
    }, LOADING_TEXTS_INTERVAL);
};

const stopRotateRandomText = () => {
    if (loadingTextInterval) {
        clearInterval(loadingTextInterval);
    }
};

onUnmounted(() => {
    stopRotateRandomText();
});

const isArchived = computed(() => {
    return task.value?.is_archived;
});

const handleDeleteArchive = async (event: Event) => {
    if (!isArchived.value) {
        toast.add({
            severity: "error",
            summary: "Unable to delete task. Task is not archived.",
        });
        return;
    }

    confirm.require({
        target: event.currentTarget as HTMLElement,
        message: "Are you sure you want to permanently delete this task?",
        icon: "pi pi-exclamation-triangle",
        rejectProps: {
            label: "Cancel",
            severity: "secondary",
            outlined: true,
        },
        acceptProps: {
            label: "Delete",
            severity: "danger",
        },
        accept: async () => {
            try {
                displaySpinner.value = true;
                await deleteTask();

                toast.add({
                    ...baseSuccessToastMessage,
                    summary: "Task has been permanently deleted",
                });
            } catch {
                toast.add({
                    severity: "error",
                    summary: "Could not delete task",
                });
            } finally {
                displaySpinner.value = false;
            }
        },
    });
};

const taskName = computed(() => {
    if (!task.value) {
        return "...";
    }

    return task.value.name || task.value.bpmn_id;
});

const generateTaskDescription = async () => {
    if (!task.value) {
        return;
    }
    await generateContent(
        task.value.identity,
        EntityType.TASK,
        description.value?.updateContent.bind(description.value)
    );
};

watch(isFetching, (newValue) => (displaySpinner.value = newValue), {
    immediate: true,
});
</script>

<style scoped lang="scss">
.page-title {
    display: flex;
    align-items: center;
    gap: 6px;

    i {
        font-size: 1.6rem;
    }

    .p-tag {
        margin-left: 0.5rem;
    }
}

section.code-preview-section {
    .code-preview-header {
        display: flex;
        justify-content: flex-end;
        margin-bottom: 1rem;
    }
}

.empty-state {
    display: flex;
    flex-direction: column;
    gap: 1em;
    align-items: center;
    text-align: center;

    > * {
        margin: 0;
    }

    .empty-icon {
        font-size: 3rem;
    }
}

.evidence-section {
    display: flex;
    flex-direction: column;
    gap: 1rem;
    margin-top: 0.75rem;
}

.evidence-toggle {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    align-self: flex-start;
    padding: 0.5rem 0.875rem;
    border: 1px solid var(--stekz-outline-variant);
    background: var(--surface-background-mild);
    transition:
        border-color 0.2s ease,
        background-color 0.2s ease;

    label {
        font-size: 0.875rem;
        font-weight: 500;
        color: var(--text-color);
        cursor: pointer;
        transition: color 0.2s ease;
    }

    &:has(.compliancy-toggle.p-toggleswitch-checked) {
        border-color: var(--stekz-compliancy-color);
        background: color-mix(
            in srgb,
            var(--stekz-compliancy-color) 12%,
            var(--surface-background-mild)
        );

        label {
            color: var(--stekz-compliancy-darker-color);
        }
    }

    &:has(.p-disabled) {
        opacity: 0.65;

        label {
            cursor: not-allowed;
        }
    }
}

.evidence-instructions {
    width: 100%;
}
</style>
